import { Input } from "@/components/UI";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import Calendar from "../Calendar";
import Loading from "@/components/Loading";
import { ButtonIcon } from "../UI/Buttons";
import { useAuth } from "@/data/context/authContext";
import ServiceRegistrationApi from "@/services/ServiceRegistrationApi";
import { AppointmentData, TimeSlot } from "@/data/interfaces/Appointments";
import SignUpStepContainer from "./SignUpStepContainer";
import { REGISTRATION_STEPS } from "@/data";
import {
  MEDNET_LOCATIONS,
  MednetCity,
  findMednetLocation,
} from "@/data/mednetLocations";
const ServiceRegistration: FC<{
  submitCallback: () => void;
  productSteps: REGISTRATION_STEPS[];
}> = ({ submitCallback, productSteps }) => {
  const [isLoading] = useState<boolean>(false);
  const { productToRegisterFor, user } = useAuth();

  // Try to derive a default city from data we already collected (order at
  // checkout, or pre-bound visit registration location).
  const checkoutCity = useMemo(() => {
    if (!productToRegisterFor) return null;
    const matchingOrder = user?.orders?.find(
      (o) => o.id === productToRegisterFor.order_id,
    );
    return matchingOrder?.city ?? productToRegisterFor.location ?? null;
  }, [productToRegisterFor, user?.orders]);

  const [appointment, setAppointment] = useState<AppointmentData | null>(null);
  const [appointmentLoading, setAppointmentLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
  const [consent, setConsent] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Pre-select a city based on what the user already gave us at checkout / sign-up.
  const initialCity = useMemo<MednetCity | "">(() => {
    return findMednetLocation(checkoutCity)?.city ?? "";
  }, [checkoutCity]);

  const [city, setCity] = useState<MednetCity | "">(initialCity);

  useEffect(() => {
    if (initialCity && !city) setCity(initialCity);
  }, [initialCity, city]);

  const selectedLocation = useMemo(() => {
    return MEDNET_LOCATIONS.find((loc) => loc.city === city);
  }, [city]);

  // (Re)load appointment data when the patient picks a city.
  useEffect(() => {
    if (!productToRegisterFor?.id || !city) {
      setAppointment(null);
      return;
    }

    let mounted = true;
    setAppointmentLoading(true);
    setLoadError(null);
    setAppointment(null);
    setSelectedDate(null);
    setSelectedTime(null);

    (async () => {
      try {
        const data = await ServiceRegistrationApi.fetchAppointmentData(
          productToRegisterFor.id,
          { city },
        );
        if (!mounted) return;
        setAppointment(data);
      } catch (e: unknown) {
        if (!mounted) return;
        const message =
          e instanceof Error
            ? e.message
            : "Nepavyko įkelti registracijos duomenų pasirinktame mieste";
        setLoadError(message);
      } finally {
        if (mounted) setAppointmentLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [productToRegisterFor?.id, city]);

  const onRegister = async (e: React.FormEvent) => {
    if (isLoading || isSubmitting) return;

    e.preventDefault();
    e.stopPropagation();
    setSubmitError(null);

    if (!appointment || !productToRegisterFor?.id) {
      setSubmitError("Trūksta vizito duomenų");
      return;
    }
    if (!city) {
      setSubmitError("Pasirinkite miestą");
      return;
    }
    if (!selectedDate || !selectedTime) {
      setSubmitError("Pasirinkite datą ir laiką");
      return;
    }
    if (!consent) {
      setSubmitError("Reikia sutikti su duomenų tvarkymu");
      return;
    }

    try {
      setIsSubmitting(true);
      await ServiceRegistrationApi.registerAppointment(
        productToRegisterFor.id,
        {
          location: appointment.connection.location,
          location_name: appointment.location.name,
          service: appointment.connection.service,
          service_name: appointment.service.name,
          worker: appointment.connection.worker,
          worker_name: appointment.worker.name,
          date: selectedDate,
          start: selectedTime.value + ":00",
        },
      );

      submitCallback();
    } catch (err: unknown) {
      const message =
        (err && typeof err === "object" && "data" in err
          ? ((err as { data?: { message?: string } }).data?.message ?? null)
          : null) ||
        (err instanceof Error ? err.message : null) ||
        "Nepavyko pateikti registracijos";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSelectDateTime = useCallback((date: string, time?: TimeSlot) => {
    setSelectedDate(date);
    setSelectedTime(time || null);
  }, []);

  const summary = useMemo(() => {
    if (!appointment || !selectedDate || !selectedTime) return null;
    return {
      locationName: selectedLocation?.fullAddress || appointment.location?.name,
      serviceName: appointment.service?.name,
      date: selectedDate,
      time: selectedTime,
    };
  }, [appointment, selectedDate, selectedTime, selectedLocation]);

  return (
    <SignUpStepContainer
      step={REGISTRATION_STEPS.MEDNET_REG}
      productSteps={productSteps}
      header="Kelias link geresnės sveikatos prasideda čia"
      description={[
        "Kilus klausimams visada galite kreiptis:",
        <div className="text-ink text-lg font-medium">
          <div>
            El. paštas - <a href="mailto:info@telomera.lt">info@telomera.lt</a>
          </div>
          <div>
            Telefono numeris - <a href="tel:+37067909369"> +370 679 09369</a>
          </div>
        </div>,
      ]}
    >
      <form
        className="mx-auto my-12 flex w-full max-w-[600px] flex-col gap-[40px]"
        onSubmit={onRegister}
      >
        <div className="flex flex-col gap-[18px]">
          <label
            htmlFor="mednet-city"
            className="relative flex flex-col gap-[9px]"
          >
            <span className="pl-5">Miestas:</span>
            <select
              id="mednet-city"
              value={city}
              onChange={(e) => setCity(e.target.value as MednetCity | "")}
              className="border-line-strong text-ink focus:ring-grey-100 focus-visible:ring-grey-100 h-[58px] w-full appearance-none rounded-[30px] border bg-white bg-[length:12px_12px] bg-[right_24px_center] bg-no-repeat px-[24px] pr-[48px] text-[16px] outline-none focus:ring-2 focus-visible:ring-2"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%23333' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='2 4 6 8 10 4'/></svg>\")",
              }}
            >
              <option value="">Pasirinkite miestą</option>
              {MEDNET_LOCATIONS.map((loc) => (
                <option key={loc.city} value={loc.city}>
                  {loc.city}
                </option>
              ))}
            </select>
          </label>

          {city && (
            <>
              <Input
                label="Paslauga:"
                type="text"
                disabled
                value={appointment?.service?.name || ""}
                name="service_name"
                isLoading={
                  !appointment?.service?.name &&
                  !!productToRegisterFor?.id &&
                  !loadError
                }
              />
              <Input
                label="Vieta:"
                type="text"
                disabled
                value={
                  selectedLocation?.fullAddress ||
                  appointment?.location?.name ||
                  ""
                }
                name="location_name"
                isLoading={
                  !appointment?.location?.name &&
                  !!productToRegisterFor?.id &&
                  !loadError
                }
              />
            </>
          )}
        </div>

        {city && (
          <>
            {loadError && (
              <div className="text-sm text-red-600">{loadError}</div>
            )}
            {(appointmentLoading ||
              (!appointment && productToRegisterFor?.id && !loadError)) && (
              <div className="flex min-h-[240px] flex-col items-center justify-center py-8">
                <Loading size={48} label="Kraunama..." />
              </div>
            )}
            {appointment && productToRegisterFor?.id && (
              <Calendar
                appointmentOptions={appointment}
                onSelect={onSelectDateTime}
                fetchSlots={(date) =>
                  ServiceRegistrationApi.getAvailableSlots(
                    productToRegisterFor.id,
                    {
                      location: appointment.connection.location,
                      service: appointment.connection.service,
                      worker: appointment.connection.worker,
                      date,
                    },
                  )
                }
              />
            )}

            {summary && (
              <div className="bg-paper border-line mt-2 flex flex-col gap-3 rounded-2xl border p-4 text-base md:text-lg">
                <h3 className="text-ink text-xl font-semibold md:text-2xl">
                  Vizito peržiūra
                </h3>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2">
                  <dt className="text-muted font-medium">Paslauga:</dt>
                  <dd className="text-ink m-0">{summary.serviceName}</dd>
                  <dt className="text-muted font-medium">Vieta:</dt>
                  <dd className="text-ink m-0">{summary.locationName}</dd>
                  <dt className="text-muted font-medium">Data:</dt>
                  <dd className="text-ink m-0">{summary.date}</dd>
                  <dt className="text-muted font-medium">Laikas:</dt>
                  <dd className="text-ink m-0">
                    {summary.time.value.slice(0, 5)}
                  </dd>
                </dl>
              </div>
            )}

            <Input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.currentTarget.checked)}
              name="consent"
              label={
                "Paspausdami mygtuką Patvirtinti, Jūs sutinkate su duomenų saugojimu ir tvarkymu šioje svetainėje / By clicking Submit, you consent to the storage and processing of data at this website"
              }
            />
            {submitError && (
              <div className="text-sm text-red-600">{submitError}</div>
            )}

            <ButtonIcon
              className="mx-auto mt-[20px] w-min invert"
              submitType={true}
              label="Registruotis"
              theme="light"
              icon="email"
              disabled={isSubmitting}
            />
          </>
        )}
      </form>
    </SignUpStepContainer>
  );
};

export default ServiceRegistration;
